import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: string;
  username: string;
  email: string;
  full_name?: string;
  tenant_id: string;
  roles: string[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // API base URL from environment variable
  const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';

  useEffect(() => {
    // Check for stored token on app start
    const storedToken = localStorage.getItem('felix_auth_token');
    const storedUser = localStorage.getItem('felix_user_info');
    
    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('felix_auth_token');
        localStorage.removeItem('felix_user_info');
      }
    }
    
    setLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      
      console.log('Attempting login to:', `${API_BASE_URL}/auth/login`);
      console.log('Login credentials:', { email, password: '***' });
      
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      console.log('Login response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Login failed:', errorData);
        return false;
      }

      const data = await response.json();
      console.log('Login response data:', data);
      
      // Handle different possible response structures
      let accessToken: string;
      let userInfo: User;
      
      if (data.access_token && data.user_info) {
        // Expected structure
        accessToken = data.access_token;
        userInfo = data.user_info;
      } else if (data.token && data.user) {
        // Alternative structure
        accessToken = data.token;
        userInfo = data.user;
      } else if (data.access_token) {
        // Only token, create minimal user info
        accessToken = data.access_token;
        userInfo = {
          id: 'admin',
          username: email.split('@')[0],
          email: email,
          tenant_id: 'default',
          roles: ['admin']
        };
      } else {
        console.error('Unexpected login response structure:', data);
        return false;
      }
      
      // Store token and user info
      localStorage.setItem('felix_auth_token', accessToken);
      localStorage.setItem('felix_user_info', JSON.stringify(userInfo));
      
      setToken(accessToken);
      setUser(userInfo);
      
      console.log('Login successful, token set:', accessToken ? 'YES' : 'NO');
      console.log('User info set:', userInfo);
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint if token exists
      if (token) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage and state regardless of API call result
      localStorage.removeItem('felix_auth_token');
      localStorage.removeItem('felix_user_info');
      setToken(null);
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!user && !!token,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
